import React from 'react';
import { sanitize } from 'dompurify';
import { Configuration } from '~/core/configuration/configuration.type';
import { SearchAction } from '~/core/state/actions';
import { createInitialState } from '~/core/state/createInitialState';
import searchSuccess from '~/core/state/reducer/searchSuccess';
import { State } from '../types/state';
import cancelAllFilters from './cancelAllFilters';
import cancelFilter from './cancelFilter';
import resetFilterControls from './resetFilterControls';
import sortResults from './sortResults';
import updateCategoriesPagination from './updateCategoriesPagination';
import updateFilterControls from './updateFilterControls';
import updateIndexFilterControls from './updateIndexFilterControls';
import updateResults from './updateResults';

export type Dispatch = React.Dispatch<SearchAction>;

const reducer =
    (clientConfig: Configuration, isDebugMode: boolean) =>
    (state: State, action: SearchAction): State => {
        if (isDebugMode) {
            console.log('SERP-REDUCER', action);
        }
        switch (action.type) {
            case 'reset-state': {
                const cleanState = createInitialState({
                    config: clientConfig,
                    searchQuery: '',
                    initialPagination: null,
                    snapshotSets: null,
                });
                // needed when resetting due to 0 search query length
                if (action.payload.exceptInstantSearch) {
                    return {
                        ...cleanState,
                        isInstantSearch: state.isInstantSearch,
                    };
                }
                return cleanState;
            }
            case 'change-search-query':
                return {
                    ...state,
                    searchQuery: sanitize(action.payload.searchQuery, { ALLOWED_TAGS: ['#text'] }),
                };
            case 'start-loading':
                return {
                    ...state,
                    isLoading: true,
                };
            case 'search-success':
                return searchSuccess(state, clientConfig, action.payload);
            case 'update-results':
                return updateResults(state, action.payload);
            case 'update-error':
                return {
                    ...state,
                    error: action.payload.error,
                    isLoading: false,
                };
            case 'update-index-filter-controls':
                return updateIndexFilterControls(state, action.payload);
            case 'update-filter-controls':
                return updateFilterControls(state, action.payload);
            case 'reset-filter-controls':
                return resetFilterControls(state, action.payload);
            case 'cancel-filter':
                return cancelFilter(state, clientConfig, action.payload);
            case 'cancel-all-filters':
                return cancelAllFilters(state, clientConfig, action.payload);
            case 'sort-results':
                return sortResults(state, action.payload);
            case 'update-categories-pagination':
                return updateCategoriesPagination(state, action.payload);
            case 'set-is-instant-search':
                return {
                    ...state,
                    isInstantSearch: action.payload.instantSearch,
                };
            case 'set-result-layout':
                return {
                    ...state,
                    resultLayout: action.payload.layout,
                };
            default:
                throw new Error(`Unknown action type: ${action['type']}`);
        }
    };

export default reducer;
